import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Sun, Moon, LogOut, User as UserIcon, Menu, MessageCircle, ShieldCheck, Cookie } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CompanySwitcher } from "@/components/CompanySwitcher";
import { WorkspaceSwitcher } from "@/components/WorkspaceSwitcher";
import { SidebarBody } from "./Sidebar";
import { HelpFab } from "@/components/help/HelpFab";
import { SupportButton } from "@/components/support/SupportButton";
import { PrayerReminder } from "./PrayerReminder";



import { tr } from "@/lib/tr";
import { useIsStaff } from "@/hooks/useStaffRoles";
export const Topbar = () => {
  const { t, i18n } = useTranslation();
  const { user, signOut } = useAuth();
  const { theme, toggle } = useTheme();
  const { isStaff } = useIsStaff();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const initials = (user?.user_metadata?.full_name || user?.email || "?").charAt(0).toUpperCase();
  const side = i18n.dir() === "rtl" ? "right" : "left";
  const isAr = i18n.language === "ar";

  return (
    <header className="sticky top-0 z-30 h-14 border-b bg-card flex items-center px-2 sm:px-4 gap-1.5 sm:gap-3 shadow-sm">
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="md:hidden shrink-0" aria-label="menu">
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side={side} className="p-0 w-64 bg-sidebar text-sidebar-foreground border-sidebar-border">
          <div className="flex flex-col h-full">
            <SidebarBody onNavigate={() => setMobileOpen(false)} />
          </div>
        </SheetContent>
      </Sheet>
      
      <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2 overflow-hidden">
        <WorkspaceSwitcher />
        <CompanySwitcher />
      </div>
      <div className="hidden sm:flex items-center gap-1.5 sm:gap-3">
        <SupportButton />
      </div>
      <div className="hidden lg:block">
        <PrayerReminder />
      </div>
      <div className="hidden sm:block">
        <HelpFab />
      </div>

      <Button variant="ghost" size="icon" onClick={toggle} aria-label="theme" className="shrink-0">
        {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
      </Button>
      <div className="shrink-0">
        <LanguageSwitcher />
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="rounded-full relative shrink-0">
            <Avatar className="h-8 w-8"><AvatarFallback>{initials}</AvatarFallback></Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel className="truncate">
            <div className="truncate">{user?.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate("/profile")}>
            <UserIcon className="me-2 h-4 w-4" />{t("nav.profile")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate("/support")} className="sm:hidden">
            <MessageCircle className="me-2 h-4 w-4" />{tr("الدعم الفني", "Support", "Support", "سپورٹ")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => navigate("/admin")}
            disabled={!isStaff}
            className={!isStaff ? "hidden" : undefined}
          >
            <ShieldCheck className="me-2 h-4 w-4" />
            {tr("لوحة الإدارة", "Admin portal", "Portail d'administration", "ایڈمن پورٹل")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => import("@/lib/cookieConsent").then((m) => m.openCookieSettings())}>
            <Cookie className="me-2 h-4 w-4" />
            {t("privacy.cookieSettings")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => signOut()}>
            <LogOut className="me-2 h-4 w-4" />{t("auth.logout")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
};
